import { MAX_PACKING_GROUPS } from "constants/common.constants";
import { PALLET_PRESETS, PALLET_TYPE } from "constants/palletOptions.constants";
import { CargoRow, Group, PalletRow } from "models/packingList";
import {
  createDefaultCargoRow,
  createDefaultGroup,
  createDefaultPackageSettings,
  createStarterCargoRows,
  getNextGroupNumber,
} from "./packingList.helpers";

import { createSlice, PayloadAction } from "@reduxjs/toolkit";

type InitialState = {
  groups: Group[];
  customPallets: PalletRow[];
  showCargoValidation: boolean;
};

const initialState: InitialState = {
  groups: [{ ...createDefaultGroup(1), cargoRows: createStarterCargoRows() }],
  customPallets: [],
  showCargoValidation: false,
};

const packingList = createSlice({
  name: "packingList",
  initialState,
  reducers: {
    addGroup: (state) => {
      if (state.groups.length >= MAX_PACKING_GROUPS) return;
      state.groups.push(createDefaultGroup(getNextGroupNumber(state.groups)));
    },
    importGroups: (state, action: PayloadAction<Group[]>) => {
      state.groups = action.payload.slice(0, MAX_PACKING_GROUPS);
      state.showCargoValidation = false;
    },
    mergeImportedGroups: (state, action: PayloadAction<{ groups: Group[]; targetGroupId?: string }>) => {
      const incoming = action.payload.groups;
      if (!incoming.length) return;

      let insertAt = state.groups.length;
      const targetId = action.payload.targetGroupId;
      let targetIndex = targetId ? state.groups.findIndex((group) => group.id === targetId) : -1;

      if (targetIndex < 0) {
        const lastIndex = state.groups.length - 1;
        const last = state.groups[lastIndex];
        if (last && last.cargoRows.length === 0) targetIndex = lastIndex;
      }

      if (targetIndex >= 0) {
        state.groups.splice(targetIndex, 1);
        insertAt = targetIndex;
      }

      const room = MAX_PACKING_GROUPS - state.groups.length;
      if (room <= 0) return;

      const takenNames = state.groups.map((group) => ({ name: group.name }));
      const uniqueIncoming = incoming.slice(0, room).map((group) => {
        const desired = group.name.trim();
        const alreadyUsed = takenNames.some((item) => item.name.trim().toLowerCase() === desired.toLowerCase());
        if (desired && !alreadyUsed) {
          takenNames.push({ name: desired });
          return group;
        }

        const name = `Group ${getNextGroupNumber(takenNames)}`;
        takenNames.push({ name });
        return { ...group, name };
      });

      state.groups.splice(insertAt, 0, ...uniqueIncoming);
      state.showCargoValidation = false;
    },
    setShowCargoValidation: (state, action: PayloadAction<boolean>) => {
      state.showCargoValidation = action.payload;
    },
    removeGroup: (state, action: PayloadAction<string>) => {
      state.groups = state.groups.filter((g) => g.id !== action.payload);
    },
    renameGroup: (state, action: PayloadAction<{ id: string; name: string }>) => {
      const group = state.groups.find((g) => g.id === action.payload.id);
      if (group) group.name = action.payload.name;
    },
    moveGroup: (state, action: PayloadAction<{ id: string; direction: "up" | "down" }>) => {
      const index = state.groups.findIndex((g) => g.id === action.payload.id);
      const newIndex = action.payload.direction === "up" ? index - 1 : index + 1;
      if (newIndex < 0 || newIndex >= state.groups.length) return;
      [state.groups[index], state.groups[newIndex]] = [state.groups[newIndex], state.groups[index]];
    },

    addCargoRow: (state, action: PayloadAction<string>) => {
      const group = state.groups.find((g) => g.id === action.payload);
      if (group) {
        const index = group.cargoRows.length;
        group.cargoRows.push(createDefaultCargoRow(index));
      }
    },
    removeCargoRow: (state, action: PayloadAction<{ groupId: string; rowId: string }>) => {
      const group = state.groups.find((g) => g.id === action.payload.groupId);
      if (group) group.cargoRows = group.cargoRows.filter((r) => r.id !== action.payload.rowId);
    },
    updateCargoRow: (state, action: PayloadAction<{ groupId: string; rowId: string } & Partial<CargoRow>>) => {
      const { groupId, rowId, ...changes } = action.payload;
      const row = state.groups.find((g) => g.id === groupId)?.cargoRows.find((r) => r.id === rowId);
      if (!row) return;

      if (changes.packageType && !changes.packageSettings) {
        changes.packageSettings = createDefaultPackageSettings(changes.packageType);
      }
      Object.assign(row, changes);
    },
    toggleUsePallets: (state, action: PayloadAction<string>) => {
      const group = state.groups.find((g) => g.id === action.payload);
      if (group) group.usePallets = !group.usePallets;
    },

    updatePalletRow: (state, action: PayloadAction<{ groupId: string } & Partial<PalletRow>>) => {
      const { groupId, ...changes } = action.payload;
      const group = state.groups.find((g) => g.id === groupId);
      if (!group) return;

      if (changes.palletType && changes.palletType in PALLET_PRESETS) {
        Object.assign(changes, PALLET_PRESETS[changes.palletType]);
      } else if (changes.palletType === PALLET_TYPE.CUSTOM_PALLETS) {
        Object.assign(changes, { length: 0, width: 0, height: 0, depth: 0, maxWeight: 0 });
      } else {
        const savedPallet = state.customPallets.find((p) => p.id === changes.palletType);
        if (savedPallet) {
          const { length, width, height, depth, maxWeight, palleteName, color } = savedPallet;
          Object.assign(changes, { length, width, height, depth, maxWeight, palleteName, color });
        }
      }

      Object.assign(group.palletRow, changes);
    },

    addCustomPallet: (state, action: PayloadAction<PalletRow>) => {
      state.customPallets.push(action.payload);
    },
    updateCustomPallet: (state, action: PayloadAction<PalletRow>) => {
      const index = state.customPallets.findIndex((p) => p.id === action.payload.id);
      if (index === -1) return;

      state.customPallets[index] = action.payload;

      state.groups.forEach((group) => {
        if (group.palletRow.palletType === action.payload.id) {
          const { length, width, height, depth, maxWeight, palleteName, color } = action.payload;
          Object.assign(group.palletRow, { length, width, height, depth, maxWeight, palleteName, color });
        }
      });
    },
    setCustomPallets: (state, action: PayloadAction<PalletRow[]>) => {
      state.customPallets = action.payload;
    },
    removeCustomPallet: (state, action: PayloadAction<string>) => {
      state.customPallets = state.customPallets.filter((p) => p.id !== action.payload);
      state.groups.forEach((group) => {
        if (group.palletRow.palletType === action.payload) {
          group.palletRow.palletType = PALLET_TYPE.EU_PALLETS;
          Object.assign(group.palletRow, PALLET_PRESETS[PALLET_TYPE.EU_PALLETS]);
        }
      });
    },
  },
});

export const {
  importGroups,
  mergeImportedGroups,
  setShowCargoValidation,
  addGroup,
  removeGroup,
  renameGroup,
  moveGroup,
  addCargoRow,
  removeCargoRow,
  updateCargoRow,
  toggleUsePallets,
  updatePalletRow,
  addCustomPallet,
  updateCustomPallet,
  setCustomPallets,
  removeCustomPallet,
} = packingList.actions;

export default packingList.reducer;
